import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CreditCard, ShieldCheck, CheckCircle2 } from "lucide-react";
import { api, useApi, errMsg, money, fmtDate } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Field, Spinner, ErrorBox } from "../components/common";

export default function Checkout() {
  const [sp, setSp] = useSearchParams();
  const { setUser } = useAuth();
  const tierKey = sp.get("tier") || "student";
  const cycle = sp.get("cycle") || "monthly";
  const { data: tiers, loading, error } = useApi("/tiers");
  const [method, setMethod] = useState("card");
  const [card, setCard] = useState({ number: "", exp: "", cvc: "", name: "" });
  const [paypal, setPaypal] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [receipt, setReceipt] = useState(null);
  if (loading) return <Spinner />;
  if (error) return <div className="kh-container py-16"><ErrorBox message={error} /></div>;
  const t = tiers.find((x) => x.key === tierKey) || tiers[1];
  const amount = cycle === "annual" ? t.annual_price : t.monthly_price;

  const pay = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await new Promise((r) => setTimeout(r, 900));
      const { data } = await api.post("/billing/checkout", { tier: t.key, cycle, method, card_number: card.number, card_name: card.name, paypal_email: paypal });
      setUser(data.user);
      setReceipt(data.payment);
      toast.success("Payment successful (test mode)");
    } catch (er) { setErr(errMsg(er)); } finally { setBusy(false); }
  };

  if (receipt)
    return (
      <div className="kh-container max-w-xl py-16">
        <div className="kh-card p-8" data-testid="checkout-success">
          <CheckCircle2 className="h-10 w-10 text-celadon-dark" aria-hidden="true" />
          <h1 className="mt-4 text-4xl">You're in.</h1>
          <p className="mt-2 text-[#4a4f59]">Your {t.name} membership is active and members-only content is unlocked.</p>
          <dl className="mt-6 grid grid-cols-2 gap-2 rounded-xl bg-sand p-4 text-sm">
            <dt>Receipt</dt><dd className="font-mono" data-testid="checkout-receipt-no">{receipt.receipt_no}</dd>
            <dt>Amount</dt><dd>{money(receipt.amount)} USD (test)</dd>
            <dt>Method</dt><dd className="capitalize">{receipt.method}</dd>
            <dt>Date</dt><dd>{fmtDate(receipt.created_at)}</dd>
          </dl>
          <div className="mt-6 flex flex-wrap gap-3"><Link to="/library" className="btn-ochre" data-testid="checkout-go-library">Browse the library</Link><Link to="/dashboard" className="btn-ghost" data-testid="checkout-go-dashboard">Go to dashboard</Link></div>
        </div>
      </div>
    );

  return (
    <div className="kh-container grid max-w-5xl gap-8 py-12 lg:grid-cols-5">
      <section className="kh-card p-6 sm:p-8 lg:col-span-3" aria-labelledby="co-h">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 id="co-h" className="text-3xl">Checkout</h1>
          <span className="rounded-full bg-[#FFF4D6] px-3 py-1 text-xs font-bold uppercase tracking-wider text-[#7a5300] ring-1 ring-[#e8c46b]" data-testid="test-mode-badge">Test mode</span>
        </div>
        <p className="mt-2 text-sm text-[#4a4f59]">Secure checkout for your Kilnhouse Guild membership.</p>
        <div role="radiogroup" aria-label="Payment method" className="mt-6 grid grid-cols-2 gap-3">
          {[["card", "Card"], ["paypal", "PayPal"]].map(([k, l]) => (
            <button type="button" role="radio" aria-checked={method === k} key={k} onClick={() => setMethod(k)} className={`rounded-xl border p-4 text-left text-sm font-semibold ${method === k ? "border-ink ring-2 ring-ink/20" : "border-line"}`} data-testid={`payment-method-${k}`}>
              {k === "card" ? <CreditCard className="mb-1 h-5 w-5" aria-hidden="true" /> : <span className="mb-1 block font-serif text-lg italic text-[#003087]">PayPal</span>}{l}
            </button>
          ))}
        </div>
        <form onSubmit={pay} className="mt-6 space-y-4" data-testid="checkout-form">
          {err && <div role="alert" className="rounded-xl bg-[#fdf2f1] p-3 text-sm text-[#7a1f16]" data-testid="checkout-error">{err}</div>}
          {method === "card" ? (
            <>
              <Field label="Name on card" id="cc-name"><input id="cc-name" required value={card.name} onChange={(e) => setCard({ ...card, name: e.target.value })} className="kh-input" data-testid="card-name-input" /></Field>
              <Field label="Card number" id="cc-num" hint="Test card: 4242 4242 4242 4242 · decline test: 4000 0000 0000 0002"><input id="cc-num" inputMode="numeric" required value={card.number} onChange={(e) => setCard({ ...card, number: e.target.value })} placeholder="4242 4242 4242 4242" className="kh-input font-mono" data-testid="card-number-input" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Expiry" id="cc-exp"><input id="cc-exp" required placeholder="12/30" value={card.exp} onChange={(e) => setCard({ ...card, exp: e.target.value })} className="kh-input" data-testid="card-expiry-input" /></Field>
                <Field label="CVC" id="cc-cvc"><input id="cc-cvc" required placeholder="123" value={card.cvc} onChange={(e) => setCard({ ...card, cvc: e.target.value })} className="kh-input" data-testid="card-cvc-input" /></Field>
              </div>
            </>
          ) : (
            <Field label="PayPal email" id="pp-email"><input id="pp-email" type="email" required value={paypal} onChange={(e) => setPaypal(e.target.value)} className="kh-input" data-testid="paypal-email-input" /></Field>
          )}
          <button disabled={busy} className="btn-primary w-full py-3" data-testid="checkout-pay-btn">{busy ? "Processing…" : method === "paypal" ? `Approve with PayPal · ${money(amount)}` : `Pay ${money(amount)}`}</button>
          <p className="flex items-center justify-center gap-1 text-xs text-[#5a5f69]"><ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />Secure checkout</p>
        </form>
      </section>
      <aside className="kh-card h-fit p-6 lg:col-span-2" data-testid="checkout-summary">
        <p className="eyebrow">Order summary</p>
        <h2 className="mt-2 text-2xl">{t.name}</h2>
        <div role="group" aria-label="Billing period" className="mt-4 inline-flex rounded-full border border-line p-1">
          {["monthly", "annual"].map((c) => <button key={c} type="button" onClick={() => setSp({ tier: t.key, cycle: c })} aria-pressed={cycle === c} className={`rounded-full px-4 py-1.5 text-xs font-semibold capitalize ${cycle === c ? "bg-ink text-paper" : ""}`} data-testid={`checkout-cycle-${c}`}>{c}</button>)}
        </div>
        <ul className="mt-4 space-y-1 text-sm text-[#4a4f59]">{t.benefits.map((b) => <li key={b}>· {b}</li>)}</ul>
        <p className="mt-6 flex justify-between border-t border-line pt-4 font-semibold"><span>Total today</span><span data-testid="checkout-total">{money(amount)}</span></p>
      </aside>
    </div>
  );
}
